import React, { useCallback, useEffect, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Formik, Form } from 'formik';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import Button from 'components/inputs/Button';
import * as Yup from 'yup';
import { CREATE_OFFERS, GET_OFFERS, UPDATE_OFFERS } from 'actions/offers';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';
import CustomDatePickerInput from 'components/inputs/DatePickerInput';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';

const OfferForm = () => {
  const dispatch = useDispatch();
  const { offerId } = useParams();
  const { t } = useTranslation();
  const { navigateTo } = useContext(NavContext);

  const offerDetail = useSelector((state) => state.offers.offerDetail);
  const profileData = useSelector((state) => state.profile.userProfile);
  const currentUserName = _.get(profileData, 'name', '');

  const initialValues =
    offerId && !_.isEmpty(offerDetail)
      ? {
          ...offerDetail,
          expiry: moment(_.get(offerDetail, 'expiry', '')).format('YYYY-MM-DD'),
        }
      : {
          code: '',
          amount: 0,
          type: '',
          max_uses_per_user: 0,
          expiry: '',
          times_used: {
            user: '',
            date: '',
          },
        };

  const getOfferDetails = useCallback((id) => {
    dispatch({ type: GET_OFFERS.REQUEST, payload: id });
  }, []);

  const addOffers = useCallback((data) => {
    dispatch({
      type: CREATE_OFFERS.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) {
          setTimeout(() => {
            navigateTo('/offers');
          }, 1000);
        }
      },
    });
  }, []);

  const editOffersDetail = useCallback((data) => {
    dispatch({
      type: UPDATE_OFFERS.REQUEST,
      payload: data,
      cb: (res) => {
        if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
          setTimeout(() => {
            navigateTo('/offers');
          }, 1000);
        }
      },
    });
  }, []);

  useEffect(() => {
    if (offerId) {
      getOfferDetails(offerId);
    }
  }, [offerId]);

  const OffersSchema = Yup.object().shape({
    code: Yup.string().required(t('offerForm.code')),
    amount: Yup.number().required(t('offerForm.amount')),
    type: Yup.string().required(t('offerForm.type')),
    expiry: Yup.string().required(t('offerForm.expiry')),
    max_uses_per_user: Yup.number().required(t('offerForm.maxUses')),
  });

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={`${offerId ? 'Edit' : 'Add'} Offer`} backTo="/offers" />
        <div className="page-content-wrapper scrollable">
          <div className="add-offer-page-main">
            <Card className="offer-form-card">
              <div className="offer-form__block">
                <Formik
                  enableReinitialize={!!offerId}
                  initialValues={initialValues}
                  validationSchema={OffersSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    if (offerId) {
                      const editOfferData = _.omit(values, ['times_used', 'tenant']);
                      editOffersDetail(editOfferData);
                    } else {
                      const addOfferData = _.omit(values, ['times_used']);
                      addOffers(addOfferData);
                    }
                    setSubmitting(false);
                  }}
                >
                  {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="offer-form__inner">
                        <Row>
                          <Col lg={6} md={12}>
                            <TextInput
                              isRequired
                              label={t('offerForm.codeLabel')}
                              placeholder={t('offerForm.codeLabel')}
                              value={values.code}
                              name="code"
                              onChange={handleChange}
                              error={errors.code && touched.code ? errors.code : null}
                            />
                          </Col>
                          <Col lg={6} md={12}>
                            <TextInput
                              isRequired
                              label={t('offerForm.amountLabel')}
                              placeholder={t('offerForm.amountLabel')}
                              type="number"
                              value={values.amount}
                              name="amount"
                              onChange={handleChange}
                              error={errors.amount && touched.amount ? errors.amount : null}
                            />
                          </Col>
                          <Col lg={6} md={12}>
                            <Select
                              isRequired
                              label={t('offerForm.offersType')}
                              placeholder={t('offerForm.selectOffer')}
                              options={[
                                { label: 'Flat', value: 'flat' },
                                { label: 'percentage', value: 'percentage' },
                              ]}
                              name="type"
                              value={values.type}
                              onChange={(val) => setFieldValue('type', val)}
                              error={errors.type && touched.type ? errors.type : null}
                            />
                          </Col>

                          <Col lg={6} md={12}>
                            <TextInput
                              isRequired
                              label={t('offerForm.maxUser')}
                              placeholder={t('offerForm.maxUser')}
                              name="max_uses_per_user"
                              value={values.max_uses_per_user}
                              type="number"
                              onChange={handleChange}
                              error={errors.max_uses_per_user && touched.max_uses_per_user ? errors.max_uses_per_user : null}
                            />
                          </Col>
                          <Col lg={6} md={6}>
                            <CustomDatePickerInput
                              isRequired
                              placeholder={t('offerForm.expiryLabel')}
                              label={t('offerForm.expiryLabel')}
                              name="expiry"
                              value={values.expiry}
                              onChange={(dateString) => setFieldValue('expiry', dateString)}
                              error={errors.expiry && touched.expiry ? errors.expiry : null}
                            />
                          </Col>
                        </Row>
                      </div>
                      <div className="offer-save--btn--block">
                        <Button type="submit" className="offer-save-btn save--btn" disabled={isSubmitting}>
                          {offerId ? 'Edit' : 'Save and Add'} {t('button.offers')}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </Card>
          </div>
          {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};
export default OfferForm;
